<script lang="ts">
  import { onMount, afterUpdate } from 'svelte';
  import { get } from 'svelte/store';
  import { GetCachedToken, me } from "@frontend/user";
  import { service } from '@frontend/services';
  import { formatMoney, formatDate, formatDateTime } from './Formats';
  import { push } from "svelte-spa-router";
  import { 
    type AuthenticatedRequestOfDataRequestCashier,
    type ApiResultOfDataResponseCashier,
    type DataRequestCashier,
    type DataResponseCashier,
    type User,
    UserRole
  } from "@frontend/walletapi";
  import { translate, currentLanguage } from "../Translator";
  import { usersByRole } from "@frontend/userlist";
  import { groups, RefreshGroups } from "@frontend/grouplist";
  
  let reportData: DataResponseCashier | null = null;
  let error: string = '';
  
  // Inicializar fechas con el día actual
  const today = new Date();
  const todayString = today.toISOString().split('T')[0];
  let startDate: string = todayString;
  let endDate: string = todayString;
  
  let loading: boolean = false;
  let cashierId: number | null = null;
  let isInitialLoad: boolean = true;
  let showMoreColumns: boolean = false;

  // Variables para el listado de cashiers (cuando es Owner)
  let allCashiers: User[] = [];
  let searchCashiers: User[] = [];
  let searchInput: string = "";
  let selectedCashier: User | null = null;
  let showAllCashiers: boolean = false;

  onMount(async () => {
    console.log('Dates initialized:', { startDate, endDate });
    
    // Verificar autenticación
    if (!$me) {
      push("/user/Login");
      return;
    }
    
    // Si es Owner o Admin, cargar listado de cashiers
    if ($me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin) {
      await RefreshGroups();
      loadCashiers();
    } else {
      // Establecer el ID del cajero logueado por defecto
      if ($me?.Id) {
        cashierId = $me.Id;
      }
      
      // Ejecutar búsqueda automáticamente al cargar
      await loadCashierReport();
    }
    
    isInitialLoad = false;
  });

  async function loadCashierReport() {
    if (!startDate || !endDate) {
      error = 'Por favor selecciona ambas fechas';
      return;
    }

    loading = true;
    error = '';

    try {
      const token = await GetCachedToken();
      if (!token) {
        error = 'No hay token disponible';
        return;
      }

      // Crear fechas en zona horaria local para evitar problemas de UTC
      const start = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T23:59:59');
      
      console.log('Processing dates:', {
        startDate,
        endDate,
        start: start.toISOString(),
        end: end.toISOString(),
        startLocal: start.toLocaleDateString(),
        endLocal: end.toLocaleDateString()
      });

      const request: AuthenticatedRequestOfDataRequestCashier = {
        Token: token,
        Data: {
          Datestart: start,
          Dateend: end,
          OperatorId: selectedCashier?.Id || $me?.Id || undefined
        }
      };

      const response: ApiResultOfDataResponseCashier = await service.walletapi.transactionbycashier(request);
      
      if (response.Success && response.Data) {
        reportData = response.Data;
        
        // Debug: Log the values to see what we're working with
        console.log('Report data received:', {
          TotalDepositMoney: reportData.TotalDepositMoney,
          TotalWithdrawMoney: reportData.TotalWithdrawMoney,
          TotalCreditMoney: reportData.TotalCreditMoney,
          TotalDebitMoney: reportData.TotalDebitMoney,
          depositProfit: (reportData.TotalDepositMoney?.InCents || 0) - (reportData.TotalWithdrawMoney?.InCents || 0),
          debitProfit: (reportData.TotalDebitMoney?.InCents || 0) - (reportData.TotalCreditMoney?.InCents || 0)
        });
      } else {
        error = response.ErrorMessage || "Error cargando reporte";
        reportData = null;
      }
    } catch (err) {
      error = "Error al cargar reporte";
      console.error("Error loading cashier report:", err);
      reportData = null;
    } finally {
      loading = false;
    }
  }




  function getOperatorName(): string {
    if (!$me) return 'Cashier Actual';
    const firstName = $me.FirstName || '';
    const lastName = $me.LastName || '';
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else {
      return `Cashier ${$me.Id}`;
    }
  }

  async function handleDateChange() {
    if (!isInitialLoad && startDate && endDate) {
      await loadCashierReport();
    }
  }

  // Funciones para el listado de cashiers (cuando es Owner o Admin)
  function loadCashiers() {
    // Filtrar cashiers por grupo y búsqueda
    usersByRole[UserRole.Cashier].subscribe((cashiers) => {
      if ($me?.Role === UserRole.Owner) {
        // Owner puede ver todos los cashiers
        allCashiers = cashiers;
      } else if ($me?.Role === UserRole.Admin) {
        // Admin solo ve cashiers de su grupo y grupos hijos
        allCashiers = cashiers.filter(isUserInMyGroupHierarchy);
      }
      filterCashiers();
    });
  }

  // Función para verificar si un usuario pertenece a mi jerarquía de grupos
  function isUserInMyGroupHierarchy(user: User): boolean {
    // Si soy Owner, veo todos los cashiers
    if ($me?.Role === UserRole.Owner) {
      return true;
    }

    // Si soy Admin, veo cashiers de mi grupo y grupos hijos
    if ($me?.Role === UserRole.Admin) {
      // Si el usuario no tiene grupo, no lo muestro
      if (!user.GroupId || !$me?.GroupId) {
        return false;
      }

      // Si está en mi grupo
      if (user.GroupId === $me.GroupId) {
        return true;
      }

      // Si está en un grupo hijo directo
      if (user.Group?.ParentId === $me.GroupId) {
        return true;
      }

      // Si está en un grupo nieto o más profundo
      let currentGroup = user.Group;
      while (currentGroup?.ParentId) {
        // Si encontramos que algún ancestro es nuestro grupo, incluimos al cashier
        if (currentGroup.ParentId === $me.GroupId) {
          return true;
        }

        // Buscar el siguiente grupo padre en el store de grupos
        currentGroup = get(groups).find(g => g.Id === currentGroup?.ParentId);
      }

      return false;
    }

    // Por defecto, no mostrar nada
    return false;
  }

  function filterCashiers() {
    if (searchInput == '') {
      searchCashiers = allCashiers;
    } else {
      let searchInputNormalized = searchInput.toLowerCase();
      searchCashiers = allCashiers.filter((cashier) =>
        cashier.Email?.toLowerCase().includes(searchInputNormalized) ||
        cashier.FirstName?.toLowerCase().includes(searchInputNormalized) ||
        cashier.LastName?.toLowerCase().includes(searchInputNormalized)
      );
    }
  }

  function clearSearch() {
    searchInput = '';
  }

  function IsSearchAndNoCashiersMatch(searchCashiers: User[]) {
    return searchInput && searchCashiers.length == 0;
  }

  async function selectCashier(cashier: User) {
    selectedCashier = cashier;
    showAllCashiers = false;
    await loadCashierReport();
  }

  async function selectAllCashiers() {
    selectedCashier = null;
    showAllCashiers = true;
    await loadCashierReport();
  }

  // Reactividad para la búsqueda de cashiers
  $: {
    if ($me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin) {
      searchInput;
      filterCashiers();
    }
  }
</script>

<div class="ui container">
  <!-- Header principal -->
  <div class="ui segment">
    <h3 class="ui header">
      <i class="chart line icon"></i>
      <div class="content">
        {#if $me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin}
          Cashier Reports
          <div class="sub header">
            <i class="users icon"></i> Select a cashier to view their transaction report
          </div>
        {:else}
          {$translate('Cashier Transaction Report')}
          <div class="sub header">
            <i class="sync icon"></i> {$translate('Analysis of transactions by cashier')}
          </div>
        {/if}
      </div>
    </h3>
  </div>

  {#if $me?.Role === UserRole.Owner || $me?.Role === UserRole.Admin}
    <!-- Listado de cashiers para Owner y Admin -->
    <div class="ui segment">
      <h4 class="ui header">
        <i class="users icon"></i>
        <div class="content">
          Select Cashier
          <div class="sub header">Choose a cashier to view their report</div>
        </div>
      </h4>
      
      <!-- Búsqueda de cashiers -->
      <div class="ui action input" style="margin-bottom: 1rem;">
        <input type="text" bind:value={searchInput} placeholder="Search cashiers..." autocomplete="off">
        <button class="ui icon button" on:click={clearSearch}>
          <i class="times icon"></i>
        </button>
      </div>

      <!-- Botón para ver todos los cashiers -->
      <div style="margin-bottom: 1rem;">
        <button 
          class="ui fluid button" 
          class:positive={showAllCashiers}
          on:click={selectAllCashiers}
        >
          <i class="users icon"></i>
          View All Cashiers Report
        </button>
      </div>

      <!-- Lista de cashiers -->
      {#if IsSearchAndNoCashiersMatch(searchCashiers)}
        <div class="ui negative message">
          <div class="header">No cashiers found</div>
        </div>
      {:else}
        <div class="ui cards">
          {#each searchCashiers ?? [] as cashier}
            <div class="card" class:positive={selectedCashier?.Id === cashier.Id}>
              <div class="content">
                <div class="header">
                  {cashier.FirstName} {cashier.LastName}
                  {#if selectedCashier?.Id === cashier.Id}
                    <i class="check circle icon" style="color: green; float: right;"></i>
                  {/if}
                </div>
                <div class="meta">
                  ID: {cashier.Id} | {cashier.Email}
                </div>
                <div class="description">
                  <strong>{$translate('Group')}:</strong> {cashier.Group?.Name ?? 'N/A'}<br>
                  <strong>Phone:</strong> {cashier.PhoneNumber ?? 'N/A'}<br>
                  <strong>Status:</strong> 
                  <span class={cashier.Enabled ? 'ui green label' : 'ui red label'}>
                    {cashier.Enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              </div>
              <div class="extra content">
                <button 
                  class="ui fluid primary button" 
                  on:click={() => selectCashier(cashier)}
                >
                  <i class="chart line icon"></i>
                  View Report
                </button>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>

    <!-- Filtros de fecha (solo se muestran cuando hay un cashier seleccionado) -->
    {#if selectedCashier || showAllCashiers}
      <div class="ui segment">
        <div class="ui form">
          <div class="fields">
            <div class="field">
              <label> {$translate('Start Date')}</label>
              <div class="ui input">
                <input type="date" bind:value={startDate} on:change={handleDateChange}>
              </div>
            </div>
            <div class="field">
              <label>{$translate('End Date')}</label>
              <div class="ui input">
                <input type="date" bind:value={endDate} on:change={handleDateChange}>
              </div>
            </div>
            <div class="field">
              <label>&nbsp;</label>
              <div class="ui buttons">
                <button 
                  class="ui primary button"
                  on:click={loadCashierReport}
                  disabled={loading}
                >
                  {#if loading}
                    <i class="spinner loading icon"></i>
                    {$translate('Loading')}
                  {:else}
                    <i class="search icon"></i>
                    {$translate('Search')}
                  {/if}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    {/if}
  {:else}
    <!-- Filtros originales para Cashier -->
    <div class="ui segment">
      <div class="ui form">
        <div class="fields">
          <div class="field">
            <label> {$translate('Start Date')}</label>
            <div class="ui input">
              <input type="date" bind:value={startDate} on:change={handleDateChange}>
            </div>
          </div>
          <div class="field">
            <label>{$translate('End Date')}</label>
            <div class="ui input">
              <input type="date" bind:value={endDate} on:change={handleDateChange}>
            </div>
          </div>
          <div class="field">
            <label>&nbsp;</label>
            <div class="ui buttons">
              <button 
                class="ui primary button"
                on:click={loadCashierReport}
                disabled={loading}
              >
                {#if loading}
                  <i class="spinner loading icon"></i>
                  {$translate('Loading')}
                {:else}
                  <i class="search icon"></i>
                  {$translate('Search')}
                {/if}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Mensaje de error -->
  {#if error}
    <div class="ui negative message">
      <i class="close icon"></i>
      <div class="header">
       {$translate('Error')}
      </div>
      <p>{error}</p>
    </div>
  {/if}

  <!-- Resumen del reporte -->
  {#if reportData && (($me?.Role !== UserRole.Owner && $me?.Role !== UserRole.Admin) || selectedCashier || showAllCashiers)}
    <div class="ui segment">
      <h4 class="ui header">
        <i class="chart bar icon"></i>
        <div class="content">
           {$translate('Report Summary')}
            <div class="sub header">
              {$translate('Period')}: {formatDateTime(new Date(startDate + 'T00:00:00'))} - {formatDateTime(new Date(endDate + 'T23:59:59'))}
              {#if selectedCashier}
                | {$translate('Cashier')}: {selectedCashier.FirstName} {selectedCashier.LastName} (ID: {selectedCashier.Id})
              {:else if showAllCashiers}
                | All Cashiers
              {:else if $me?.Id}
                | {$translate('Cashier')}: {getOperatorName()}
              {/if}
            </div>
        </div>
      </h4>

      <!-- Tabla de resumen de transacciones -->
      <div class="ui segment">

        <div class="ui table">
          {#if $me?.Role === UserRole.Admin || $me?.Role === UserRole.Owner}
            <div class="ui right floated button" on:click={() => showMoreColumns = !showMoreColumns}>
              <i class={showMoreColumns ? 'chevron up icon' : 'chevron down icon'}></i>
              {$translate(showMoreColumns ? 'Less' : 'More')}
            </div>
          {/if}
          
          <table class="ui celled table">
            <thead>
              <tr>
                <th>{$translate('Groups')}</th>
                <th>{$translate('Deposit')}</th>
                <th>{$translate('Withdraw')}</th>
                <th>{$translate('Profit')}</th>
                <th>{$translate('Debit')}</th>
                <th>{$translate('Credit')}</th>
                <th>{$translate('Profit')}</th>
                {#if showMoreColumns && ($me?.Role === UserRole.Admin || $me?.Role === UserRole.Owner)}
                  <th>{$translate('Promo')}</th>
                  <th>{$translate('End Round')}</th>
                  <th>{$translate('Cancel Bet')}</th>
                  <th>{$translate('Deposit Cancel')}</th>
                  <th>{$translate('Withdraw Cancel')}</th>
                {/if}
              </tr>
            </thead>
            <tbody>
                <tr>
                  <td>
                    <strong>
                      {#if selectedCashier}
                        {selectedCashier.FirstName} {selectedCashier.LastName}
                      {:else if showAllCashiers}
                        All Cashiers
                      {:else}
                        {getOperatorName()}
                      {/if}
                    </strong>
                    {#if !selectedCashier && !showAllCashiers}
                      <div class="ui mini green label">Current</div>
                    {/if}
                  </td>
                <!-- Deposit -->
                <td class="positive">
                  <strong>
                    {#if reportData.TotalDepositMoney}
                      {formatMoney(reportData.TotalDepositMoney)}
                    {:else}
                      $0.00
                    {/if}
                  </strong>
                </td>
                <!-- Withdraw -->
                <td class="negative">
                  <strong>
                    {#if reportData.TotalWithdrawMoney}
                      {formatMoney(reportData.TotalWithdrawMoney)}
                    {:else}
                      $0.00
                    {/if}
                  </strong>
                </td>
                <!-- Profit (Deposit - Withdraw) -->
                <td class={((reportData.TotalDepositMoney?.InCents || 0) - (reportData.TotalWithdrawMoney?.InCents || 0)) >= 0 ? 'positive' : 'negative'}>
                  <strong>
                    {formatMoney((reportData.TotalDepositMoney?.InCents || 0) - (reportData.TotalWithdrawMoney?.InCents || 0))}
                  </strong>
                </td>
                <!-- Debit -->
                <td class="negative">
                  {#if reportData.TotalDebitMoney}
                    {formatMoney(reportData.TotalDebitMoney)}
                  {:else}
                    $0.00
                  {/if}
                </td>
                <!-- Credit -->
                <td class="positive">
                  {#if reportData.TotalCreditMoney}
                    {formatMoney(reportData.TotalCreditMoney)}
                  {:else}
                    $0.00
                  {/if}
                </td>
                <!-- Profit (Debit - Credit) -->
                <td class={((reportData.TotalDebitMoney?.InCents || 0) - (reportData.TotalCreditMoney?.InCents || 0)) >= 0 ? 'positive' : 'negative'}>
                  <strong>
                    {formatMoney((reportData.TotalDebitMoney?.InCents || 0) - (reportData.TotalCreditMoney?.InCents || 0))}
                  </strong>
                </td>
                {#if showMoreColumns && ($me?.Role === UserRole.Admin || $me?.Role === UserRole.Owner)}
                  <!-- Promo -->
                  <td class="positive">
                    {#if reportData.TotalDepositPromo}
                      {formatMoney(reportData.TotalDepositPromo)}
                    {:else}
                      $0.00
                    {/if}
                  </td>
                  <!-- End Round -->
                  <td>
                    {#if reportData.TotalEndRoundMoney}
                      {formatMoney(reportData.TotalEndRoundMoney)}
                    {:else}
                      $0.00
                    {/if}
                  </td>
                  <!-- Cancel Bet -->
                  <td>
                    {#if reportData.TotalCancelBetMoney}
                      {formatMoney(reportData.TotalCancelBetMoney)}
                    {:else}
                      $0.00
                    {/if}
                  </td>
                  <!-- Deposit Cancel -->
                  <td>
                    {#if reportData.TotalDepositCancelMoney}
                      {formatMoney(reportData.TotalDepositCancelMoney)}
                    {:else}
                      $0.00
                    {/if}
                  </td>
                  <!-- Withdraw Cancel -->
                  <td>
                    {#if reportData.TotalWithdrawCancelMoney}
                      {formatMoney(reportData.TotalWithdrawCancelMoney)}
                    {:else}
                      $0.00
                    {/if}
                  </td>
                {/if}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>
  {/if}

  <!-- Mensaje cuando no hay datos -->
  {#if !reportData && !loading && !error && (($me?.Role !== UserRole.Owner && $me?.Role !== UserRole.Admin) || selectedCashier || showAllCashiers)}
    <div class="ui segment">
      <div class="ui center aligned basic segment">
        <i class="chart bar icon" style="font-size: 4em; color: #ccc;"></i>
        <h3 class="ui header">
          No hay datos para mostrar
          <div class="sub header">
            Selecciona un rango de fechas y haz clic en "Buscar" para generar el reporte.
          </div>
        </h3>
        <div class="ui buttons">
          <button 
            class="ui primary button"
            on:click={loadCashierReport}
            disabled={!startDate || !endDate}
          >
            <i class="search icon"></i>
            Generar Reporte
          </button>
        </div>
      </div>
    </div>
  {/if}
</div>